import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import { alpha } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { CheckIcon, CloseIcon } from 'assets/icons';
import { PrimaryButton, SecondaryButton } from 'components/forms/atoms/FormButtons';
import FormField from 'components/forms/atoms/FormField';
import InlineFeedback from 'components/forms/molecules/InlineFeedback';
import { useFeedback } from 'contexts/FeedbackContext';
import { useEffect, useState } from 'react';
import {
  useActivateBusinessUnitPromptVersionMutation,
  useGetBusinessUnitPromptVersionQuery,
  useGetBusinessUnitPromptVersionsQuery,
} from 'services/api/businessUnitPromptVersions.api';
import type {
  BusinessUnitPromptVersion,
  BusinessUnitPromptVersionMetadata,
} from 'services/types/businessUnitPromptVersion';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  STATUS_GOOD,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  SURFACE_HOVER,
  YELLOW,
} from 'styles/tokens';
import { getMutationErrorMessage } from 'utils/mutationError';

import PromptVersionDiff from './PromptVersionDiff';

interface Props {
  open: boolean;
  businessUnitId: number;
  activeVersionId: number | null;
  isDirty: boolean;
  editorSnapshot: {
    prompt: string | null;
    availableFunctions: string[] | null;
    parentVersionId: number | null;
    sectionSnapshot: null;
  } | null;
  fallbackBaseSnapshot: {
    prompt: string | null;
    availableFunctions: string[] | null;
    sectionSnapshot: null;
  };
  isSavingEditorSnapshot: boolean;
  onClose: () => void;
  onSaveEditorSnapshot: () => void;
  onActivated: (version: BusinessUnitPromptVersion) => void;
  onRefetch: () => Promise<unknown>;
}

const EDITOR_COMPARE_ID = -1;
const PAGE_SIZE = 20;

type PendingAction = { kind: 'activate'; version: BusinessUnitPromptVersion } | null;

const authorLabel = (version: BusinessUnitPromptVersionMetadata): string =>
  version.createdBy?.name || version.createdBy?.email || 'Sistema';

const PromptVersionHistoryDialog = ({
  open,
  businessUnitId,
  activeVersionId,
  isDirty,
  editorSnapshot,
  fallbackBaseSnapshot,
  isSavingEditorSnapshot,
  onClose,
  onSaveEditorSnapshot,
  onActivated,
  onRefetch,
}: Props) => {
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [baseId, setBaseId] = useState(0);
  const [compareId, setCompareId] = useState(0);
  const [layout, setLayout] = useState<'unified' | 'split'>('unified');
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { notifySaved } = useFeedback();
  const [activateVersion, { isLoading: isActivating }] = useActivateBusinessUnitPromptVersionMutation();
  const { currentData: versionsPage, isFetching: isFetchingVersions } = useGetBusinessUnitPromptVersionsQuery(
    { businessUnitId, limit: PAGE_SIZE, offset: page * PAGE_SIZE, search: appliedSearch },
    { skip: !open }
  );
  const versions = versionsPage?.versions ?? [];
  const total = versionsPage?.total ?? 0;
  const { data: baseDetail, isFetching: isFetchingBase } = useGetBusinessUnitPromptVersionQuery(
    { businessUnitId, versionId: baseId },
    { skip: !open || baseId <= 0 }
  );
  const { data: compareDetail, isFetching: isFetchingCompare } = useGetBusinessUnitPromptVersionQuery(
    { businessUnitId, versionId: compareId },
    { skip: !open || compareId <= 0 }
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      setAppliedSearch(search.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  // Derived scalars, not the object: the parent rebuilds editorSnapshot on every render.
  const hasEditorSnapshot = Boolean(editorSnapshot);
  const editorParentVersionId = editorSnapshot?.parentVersionId ?? null;
  const newestVersionId = versions[0]?.id ?? 0;

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setAppliedSearch('');
    setPage(0);
    setPendingAction(null);
    setErrorMessage(null);
    setCompareId(hasEditorSnapshot ? EDITOR_COMPARE_ID : (activeVersionId ?? 0));
    setBaseId(hasEditorSnapshot ? (editorParentVersionId ?? activeVersionId ?? 0) : 0);
  }, [open, hasEditorSnapshot, editorParentVersionId, activeVersionId]);

  // No active version (or it is gone): fall back to the newest one the list returned.
  useEffect(() => {
    if (open && compareId === 0 && newestVersionId) setCompareId(newestVersionId);
  }, [open, compareId, newestVersionId]);

  const selectVersion = (version: BusinessUnitPromptVersionMetadata) => {
    setCompareId(version.id);
    setBaseId(version.parentVersionId ?? 0);
    setErrorMessage(null);
  };

  const runAction = async (action: Exclude<PendingAction, null>) => {
    setPendingAction(null);
    setErrorMessage(null);
    try {
      const activated = await activateVersion({
        businessUnitId,
        versionId: action.version.id,
        expectedActiveVersionId: activeVersionId,
      }).unwrap();
      notifySaved('Versión activada');
      onActivated(activated);
      await onRefetch();
    } catch (error) {
      setErrorMessage(getMutationErrorMessage(error, 'No fue posible activar la versión.'));
    }
  };

  const requestAction = (action: Exclude<PendingAction, null>) => {
    if (isDirty) setPendingAction(action);
    else void runAction(action);
  };

  const isComparingEditor = compareId === EDITOR_COMPARE_ID && Boolean(editorSnapshot);
  const compareSnapshot = isComparingEditor ? editorSnapshot : compareDetail;
  const baseSnapshot = baseId > 0 ? baseDetail : fallbackBaseSnapshot;
  const isLoadingDiff = (baseId > 0 && isFetchingBase) || (!isComparingEditor && isFetchingCompare);
  const rangeStart = total ? page * PAGE_SIZE + 1 : 0;
  const rangeEnd = page * PAGE_SIZE + versions.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      slotProps={{
        paper: {
          sx: {
            height: { xs: '92vh', md: '88vh' },
            maxHeight: '94vh',
            overflow: 'hidden',
            borderRadius: RADIUS_CARD,
            backgroundColor: GLASS_BACKGROUND,
            backdropFilter: GLASS_BLUR,
            boxShadow: SHADOW,
            fontFamily: 'Jost, sans-serif',
          },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2 }}>
        <Typography variant="h5" sx={{ color: NAVY, fontWeight: 700 }}>
          Historial de prompt y tools
        </Typography>
        <IconButton onClick={onClose} aria-label="Cerrar historial" sx={{ color: NAVY }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ px: 3, pb: 2, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0, overflow: 'hidden' }}
      >
        {pendingAction && (
          <InlineFeedback severity="warning">
            <Typography sx={{ color: NAVY, fontWeight: 600 }}>Tienes cambios sin guardar</Typography>
            <Typography variant="body2" sx={{ color: alpha(NAVY, 0.68), mt: 0.5 }}>
              Si continúas, se descartarán los cambios locales del prompt y las tools.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              <SecondaryButton onClick={() => setPendingAction(null)} sx={{ py: 0.65 }}>
                Conservar cambios
              </SecondaryButton>
              <PrimaryButton onClick={() => void runAction(pendingAction)} sx={{ py: 0.65 }}>
                Descartar y continuar
              </PrimaryButton>
            </Box>
          </InlineFeedback>
        )}
        {errorMessage && <InlineFeedback severity="error">{errorMessage}</InlineFeedback>}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '300px minmax(0, 1fr)' },
            gridTemplateRows: { xs: 'minmax(180px, 0.45fr) minmax(0, 1fr)', md: 'minmax(0, 1fr)' },
            gap: 2,
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              bgcolor: SURFACE_CONTAINER_LOW,
              borderRadius: RADIUS_CARD,
              p: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <FormField
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por nota o #id"
              size="small"
              aria-label="Buscar versiones"
            />

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {editorSnapshot && !appliedSearch && (
                <Box
                  component="button"
                  type="button"
                  onClick={() => {
                    setCompareId(EDITOR_COMPARE_ID);
                    setBaseId(editorParentVersionId ?? activeVersionId ?? newestVersionId);
                  }}
                  sx={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    border: 0,
                    bgcolor: compareId === EDITOR_COMPARE_ID ? SURFACE_CONTAINER_LOWEST : 'transparent',
                    borderRadius: RADIUS_ITEM,
                    p: 1.5,
                    mb: 0.75,
                    color: NAVY,
                    cursor: 'pointer',
                    fontFamily: 'Jost, sans-serif',
                    boxShadow: compareId === EDITOR_COMPARE_ID ? SHADOW : 'none',
                    '&:hover': { bgcolor: SURFACE_CONTAINER_LOWEST },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography component="span" sx={{ fontWeight: 700 }}>
                      Cambios actuales
                    </Typography>
                    <Chip
                      label="Sin guardar"
                      size="small"
                      sx={{ color: NAVY, bgcolor: alpha(YELLOW, 0.2), fontWeight: 600 }}
                    />
                  </Box>
                  <Typography variant="caption" component="div" sx={{ color: alpha(NAVY, 0.6), mt: 0.75 }}>
                    Prompt y tools del editor
                  </Typography>
                </Box>
              )}
              {!versions.length && !isFetchingVersions && (
                <Typography variant="body2" sx={{ color: alpha(NAVY, 0.6), p: 1.5 }}>
                  Sin versiones para esta búsqueda.
                </Typography>
              )}
              {versions.map((version) => {
                const isActive = version.id === activeVersionId;
                const selected = version.id === compareId;
                return (
                  <Box
                    component="button"
                    type="button"
                    key={version.id}
                    onClick={() => selectVersion(version)}
                    sx={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      border: 0,
                      bgcolor: selected ? SURFACE_CONTAINER_LOWEST : 'transparent',
                      borderRadius: RADIUS_ITEM,
                      p: 1.5,
                      mb: 0.75,
                      color: NAVY,
                      cursor: 'pointer',
                      fontFamily: 'Jost, sans-serif',
                      boxShadow: selected ? SHADOW : 'none',
                      '&:hover': { bgcolor: selected ? SURFACE_CONTAINER_LOWEST : SURFACE_HOVER },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Typography component="span" sx={{ fontWeight: 700 }}>
                        #{version.id}
                      </Typography>
                      <Chip
                        label={isActive ? 'Activa' : 'Inactiva'}
                        size="small"
                        sx={{
                          color: isActive ? STATUS_GOOD : NAVY,
                          bgcolor: alpha(isActive ? STATUS_GOOD : NAVY, isActive ? 0.12 : 0.08),
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    <Typography variant="caption" component="div" sx={{ color: alpha(NAVY, 0.6), mt: 0.75 }}>
                      {authorLabel(version)} · {new Date(version.createdAt).toLocaleString('es-CL', { hour12: false })}
                    </Typography>
                    {version.note && (
                      <Typography variant="body2" component="div" sx={{ color: NAVY, mt: 0.75 }}>
                        {version.note}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 0.5 }}>
              <Typography variant="caption" sx={{ color: alpha(NAVY, 0.6) }}>
                {total ? `${rangeStart}–${rangeEnd} de ${total}` : '0 versiones'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <SecondaryButton
                  onClick={() => setPage((current) => Math.max(current - 1, 0))}
                  disabled={page === 0 || isFetchingVersions}
                  sx={{ py: 0.4, px: 1.25, minWidth: 0 }}
                >
                  Anterior
                </SecondaryButton>
                <SecondaryButton
                  onClick={() => setPage((current) => current + 1)}
                  disabled={rangeEnd >= total || isFetchingVersions}
                  sx={{ py: 0.4, px: 1.25, minWidth: 0 }}
                >
                  Siguiente
                </SecondaryButton>
              </Box>
            </Box>
          </Box>

          <Box sx={{ minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 1.5 }}>
              <FormField
                select
                label="Versión base"
                value={baseId}
                onChange={(event) => setBaseId(Number(event.target.value))}
                fullWidth={false}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value={0}>Configuración actual</MenuItem>
                {baseId > 0 && !versions.some((version) => version.id === baseId) && (
                  <MenuItem value={baseId}>#{baseId}</MenuItem>
                )}
                {versions.map((version) => (
                  <MenuItem key={version.id} value={version.id}>
                    #{version.id}
                  </MenuItem>
                ))}
              </FormField>
              <Typography sx={{ color: NAVY, fontWeight: 600, pb: 1 }}>
                → {isComparingEditor ? 'Cambios sin guardar' : `#${compareId}`}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={layout}
                onChange={(_, value: 'unified' | 'split' | null) => value && setLayout(value)}
                aria-label="Vista del diff"
                sx={{ ml: 'auto' }}
              >
                <ToggleButton value="unified">Unificado</ToggleButton>
                <ToggleButton value="split">Dividido</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {!isComparingEditor && compareDetail && !compareDetail.validation.isValid && (
              <InlineFeedback severity="error">
                <Typography sx={{ fontWeight: 600 }}>Esta versión no se puede activar</Typography>
                <Typography variant="body2">{compareDetail.validation.message}</Typography>
                {!!compareDetail.validation.invalidFunctions?.length && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Tools no disponibles: {compareDetail.validation.invalidFunctions.join(', ')}
                  </Typography>
                )}
              </InlineFeedback>
            )}

            <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'auto' }}>
              {isLoadingDiff || !baseSnapshot || !compareSnapshot ? (
                <Box sx={{ bgcolor: SURFACE_CONTAINER_LOW, borderRadius: RADIUS_ITEM, height: 320 }} />
              ) : (
                <PromptVersionDiff
                  oldPrompt={baseSnapshot.prompt}
                  newPrompt={compareSnapshot.prompt}
                  oldFunctions={baseSnapshot.availableFunctions}
                  newFunctions={compareSnapshot.availableFunctions}
                  oldSectionSnapshot={baseSnapshot.sectionSnapshot ?? null}
                  newSectionSnapshot={compareSnapshot.sectionSnapshot ?? null}
                  layout={layout}
                />
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: SURFACE_CONTAINER_LOW, gap: 1 }}>
        {!pendingAction && isComparingEditor && (
          <PrimaryButton
            startIcon={<CheckIcon />}
            disabled={isSavingEditorSnapshot}
            onClick={onSaveEditorSnapshot}
            sx={{ bgcolor: YELLOW }}
          >
            {isSavingEditorSnapshot ? 'Guardando…' : 'Guardar versión'}
          </PrimaryButton>
        )}
        {!pendingAction && !isComparingEditor && compareDetail && compareDetail.id !== activeVersionId && (
          <PrimaryButton
            startIcon={<CheckIcon />}
            disabled={isActivating || !compareDetail.validation.isValid}
            onClick={() => requestAction({ kind: 'activate', version: compareDetail })}
            sx={{ bgcolor: YELLOW }}
          >
            {isActivating ? 'Activando…' : `Activar #${compareDetail.id}`}
          </PrimaryButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PromptVersionHistoryDialog;
